import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AdminChrome from "../components/AdminChrome";
import { useRequireAdmin } from "./AdminLogin";

const KINDS = [
  { id: "ip", label: "single IP", placeholder: "203.0.113.42" },
  { id: "cidr", label: "CIDR range", placeholder: "203.0.113.0/24" },
  { id: "ua", label: "user-agent fragment", placeholder: "BadBot/1.0" },
  { id: "country", label: "country (ISO)", placeholder: "RU" },
];

export default function AdminModeration() {
  useRequireAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("ip");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [check, setCheck] = useState({ ip: "", ua: "" });
  const [checkResult, setCheckResult] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blocks");
      setItems(data.items || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setCreating(true);
    try {
      await api.post("/admin/blocks", { kind, value: value.trim(), reason: reason.trim() });
      setValue(""); setReason("");
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || "could not create block");
    } finally { setCreating(false); }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Remove block "${label}"?`)) return;
    try { await api.delete(`/admin/blocks/${id}`); fetchAll(); }
    catch (e) { alert(e.response?.data?.detail || "delete failed"); }
  };

  const runCheck = async (e) => {
    e.preventDefault();
    if (!check.ip.trim()) return;
    try {
      const { data } = await api.get("/admin/blocks/check", { params: { ip: check.ip.trim(), ua: check.ua } });
      setCheckResult(data);
    } catch (err) {
      setCheckResult({ blocked: false, error: err.response?.data?.detail || "check failed" });
    }
  };

  const filtered = search
    ? items.filter((b) =>
        [b.kind, b.value, b.reason].join(" ").toLowerCase().includes(search.toLowerCase())
      )
    : items;

  return (
    <div className="App">
      <Header />
      <main className="xs-page" data-testid="admin-moderation-page">
        <div className="xs-container">
          <AdminChrome />
          <h1 style={{ margin: "0 0 4px", color: "#ffde16" }}>moderation / blocks</h1>

          <p style={{ opacity: 0.85, marginTop: 12, fontSize: "0.9em" }}>
            blocked visitors get a clean "access restricted" page on every API call. country blocking
            uses cached ipapi.co lookups (7-day TTL) and only runs when at least one country block exists.
          </p>

          <form onSubmit={create} style={{
            border: "1px solid #ffde16", borderRadius: 8, padding: 16, marginTop: 16,
            background: "rgba(0, 0, 0, 0.5)",
          }}>
            <h3 className="xs-section-title" style={{ marginTop: 0 }}>add a block</h3>
            <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 10 }}>
              <select className="xs-select" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="block-kind">
                {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
              <input
                className="xs-input"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={KINDS.find((k) => k.id === kind)?.placeholder}
                data-testid="block-value"
              />
            </div>
            <input
              className="xs-input"
              style={{ marginTop: 10 }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="reason / note (optional)"
              data-testid="block-reason"
            />
            <div style={{ marginTop: 12 }}>
              <button type="submit" className="xs-btn primary" disabled={creating || !value.trim()} data-testid="block-create">
                {creating ? "…" : "add block"}
              </button>
            </div>
          </form>

          <form onSubmit={runCheck} style={{
            border: "1px dashed rgba(255,222,22,0.5)", borderRadius: 8, padding: 16, marginTop: 16,
          }}>
            <h3 className="xs-section-title" style={{ marginTop: 0 }}>test a visitor</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10 }}>
              <input className="xs-input" placeholder="ip address (e.g. 8.8.8.8)" value={check.ip} onChange={(e) => setCheck({ ...check, ip: e.target.value })} data-testid="check-ip" />
              <input className="xs-input" placeholder="user-agent (optional)" value={check.ua} onChange={(e) => setCheck({ ...check, ua: e.target.value })} data-testid="check-ua" />
              <button type="submit" className="xs-btn" data-testid="check-run">test</button>
            </div>
            {checkResult && (
              <div style={{ marginTop: 10, fontSize: "0.85em", color: checkResult.blocked ? "#ff9b6b" : "#6bff8a" }} data-testid="check-result">
                {checkResult.blocked
                  ? `🚫 BLOCKED — ${checkResult.reason}`
                  : "✓ allowed"}
              </div>
            )}
          </form>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "20px 0 8px", gap: 10 }}>
            <h3 className="xs-section-title" style={{ margin: 0, paddingBottom: 0, border: 0 }}>active blocks ({items.length})</h3>
            <input
              type="search"
              className="xs-input"
              placeholder="filter…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 240 }}
              data-testid="block-search"
            />
          </div>

          <div style={{ border: "1px solid #ffde16", borderRadius: 8, overflow: "auto" }}>
            {loading && <div style={{ padding: 24, opacity: 0.6 }}>loading…</div>}
            {!loading && filtered.length === 0 && (
              <div style={{ padding: 24, opacity: 0.6 }}>no blocks active.</div>
            )}
            {!loading && filtered.length > 0 && (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85em" }}>
                <thead>
                  <tr style={{ background: "rgba(0,0,0,0.6)", color: "#ffde16" }}>
                    <th style={{ textAlign: "left", padding: "10px 12px" }}>kind</th>
                    <th style={{ textAlign: "left", padding: "10px 12px" }}>value</th>
                    <th style={{ textAlign: "left", padding: "10px 12px" }}>reason</th>
                    <th style={{ textAlign: "left", padding: "10px 12px" }}>added</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr key={b.id} style={{ borderTop: "1px solid rgba(255,222,22,0.15)" }} data-testid={`block-row-${b.id}`}>
                      <td style={{ padding: "10px 12px" }}><code style={{ color: "#ffde16" }}>{b.kind}</code></td>
                      <td style={{ padding: "10px 12px", color: "#fffbd4" }}><code>{b.value}</code></td>
                      <td style={{ padding: "10px 12px", opacity: 0.8 }}>{b.reason || "—"}</td>
                      <td style={{ padding: "10px 12px", opacity: 0.7 }}>{new Date(b.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>
                        <button className="xs-btn danger" style={{ padding: "4px 10px", minHeight: 0, fontSize: "0.85em" }} onClick={() => remove(b.id, `${b.kind}:${b.value}`)}>remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
      <Footer show />
    </div>
  );
}
